import {StateCreator} from 'zustand'
import {IPanelTheme} from '../../utils/themeUtil'
import type {
  IUser,
  IWorkforce,
  IAccountDetails,
  IProductSwitcherCategory,
  IProductSwitcherResponse,
} from '../../types'

export interface IUserStore {
  currentUser: IUser | null
  selectedLanguage: string
  languageCache: Set<string>
  productSwitcher: IProductSwitcherCategory[]
  accountDetails: IAccountDetails
  productSwitcherWorkforcePermission: boolean
  productSwitcherEmpowerPermission: boolean
  workforces: IWorkforce[]
  selectedWorkforceName: string | null
  selectedWorkforce: IWorkforce | Record<string, never>
  emailDisabled: boolean
  prevPath: string
  panelTheme: IPanelTheme | null
  isRedirectingToLogin: boolean

  // setters used by BaseLayout to sync React Query data into the store
  setCurrentUser: (userData: IUser) => void
  setWorkforces: (workforces: IWorkforce[]) => void
  setProductSwitcher: (data: IProductSwitcherResponse) => void
  setPanelTheme: (theme: IPanelTheme | null) => void
  setIsRedirectingToLogin: (value: boolean) => void

  // pure UI setters (unchanged)
  changeWorkforceName: (name: string) => void
  sortWorkforces: (workforces: IWorkforce[]) => void
  setSelectWorkforce: (workforce: IWorkforce) => void
  setSelectedLanguage: (lang: string) => void
  setPrevPath: (path: string) => void
}

const admins = [
  'templates@questionpro.com',
  'heghine.mkrtchyan@questionpro.com',
]

// The CA backend serializes isAdminMember as the string "true"/"false"
// instead of a boolean. A plain truthy check treats any non-empty string
// (including "false") as an admin, so this coerces string values explicitly.
const toBooleanFlag = (value: boolean | string | undefined): boolean =>
  typeof value === 'string' ? value.toLowerCase() === 'true' : Boolean(value)

export const createUserSlice: StateCreator<IUserStore, [], [], IUserStore> = (
  set,
  get,
) => ({
  currentUser: null,
  selectedLanguage: 'en',
  languageCache: new Set(['en']),
  productSwitcher: [],
  accountDetails: {},
  productSwitcherWorkforcePermission: true,
  productSwitcherEmpowerPermission: true,
  workforces: [],
  selectedWorkforceName:
    typeof window !== 'undefined'
      ? localStorage.getItem('selectedWorkforceName')
      : null,
  selectedWorkforce:
    typeof window !== 'undefined' && localStorage.getItem('selectedWorkforce')
      ? JSON.parse(localStorage.getItem('selectedWorkforce')!)
      : {},
  emailDisabled: true,
  prevPath: '/',
  panelTheme: null,
  isRedirectingToLogin: false,

  setCurrentUser: (userData: IUser): void => {
    const isAdmin = admins.includes(userData.emailAddress)
      ? true
      : toBooleanFlag(userData.isAdminMember)
    const currentUser = {
      ...userData,
      isAdmin,
      firstName: userData.firstname,
      lastName: userData.lastname,
    }
    set({currentUser, selectedLanguage: userData.language})
  },

  setWorkforces: (workforces: IWorkforce[]): void =>
    set(state => {
      const workforce =
        workforces.find(
          (w: IWorkforce) =>
            w.workforceID === state.currentUser?.lastVisitedWorkforceID,
        ) || workforces[0]
      if (typeof window !== 'undefined' && workforce) {
        localStorage.setItem('selectedWorkforceName', workforce.workforceName)
        localStorage.setItem('selectedWorkforce', JSON.stringify(workforce))
      }
      return {
        workforces,
        selectedWorkforce: workforce || {},
        selectedWorkforceName: workforce?.workforceName || null,
      }
    }),

  setProductSwitcher: (data: IProductSwitcherResponse): void => {
    const permissions = data?.headerInfo?.[0]?.productSwitcher?.categories
    let workforcePermission = true
    let empowerPermission = true
    permissions?.forEach((products: IProductSwitcherCategory) => {
      if (products?.name === 'Workforce' && !products?.active)
        workforcePermission = false
      if (products?.name === 'Customer Experience') {
        products?.products?.forEach(product => {
          if (product?.name === 'Empower' && !product?.active)
            empowerPermission = false
        })
      }
    })
    set({
      productSwitcher: permissions || [],
      accountDetails: data?.headerInfo?.[1]?.myAccount || {},
      productSwitcherWorkforcePermission: workforcePermission,
      productSwitcherEmpowerPermission: empowerPermission,
    })
  },

  setPanelTheme: (theme: IPanelTheme | null): void => set({panelTheme: theme}),

  setIsRedirectingToLogin: (value: boolean): void =>
    set({isRedirectingToLogin: value}),

  changeWorkforceName: (name: string): void => {
    if (typeof window !== 'undefined')
      localStorage.setItem('selectedWorkforceName', name)
    set({selectedWorkforceName: name})
  },

  sortWorkforces: (workforces: IWorkforce[]): void => set({workforces}),

  setSelectWorkforce: (workforce: IWorkforce): void => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('selectedWorkforce', JSON.stringify(workforce))
    }
    set({selectedWorkforce: workforce})
  },

  setSelectedLanguage: (lang: string): void => {
    const langCache = get().languageCache
    if (lang !== 'en' && !langCache.has(lang)) {
      langCache.add(lang)
    }
    set({selectedLanguage: lang, languageCache: langCache})
  },

  setPrevPath: (path: string): void => set({prevPath: path}),
})
